import { useState } from 'react'
import { X } from '@/components/icons'
import { Button } from './ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { useChecklistTemplates } from '../lib/hooks'
import { ListSkeleton } from './ui/skeleton-presets'
import { SearchInput } from './ui/search-input'

interface ChecklistPickerDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (templateId: string) => void
  title?: string
}

export function ChecklistPickerDrawer({
  isOpen,
  onClose,
  onSelect,
  title = 'Select Task',
}: ChecklistPickerDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const { data: templates = [], isPending: loading } = useChecklistTemplates()

  const filtered = templates.filter((t) =>
    (t.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleClose = () => {
    setSearchQuery('')
    onClose()
  }

  const handleSelect = (templateId: string) => {
    onSelect(templateId)
    handleClose()
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent className="bg-surface-L0 rounded-t-[10px] max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 border-b border-border-L2">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {title}
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div className="flex-1 overflow-hidden px-4 pt-4">
          <div className="flex flex-col gap-2 h-full">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search checklists"
            />

            <div className="flex-1 overflow-y-auto overscroll-contain pb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
              {loading ? (
                <ListSkeleton />
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <p className="text-sm text-content-primary-subtle text-center">
                    {searchQuery ? 'No checklists found matching your search' : 'No checklists available'}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                  {filtered.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleSelect(template.id)}
                      className="w-full flex flex-col items-start gap-0.5 min-h-9 px-3 py-2 rounded-md transition-colors active:bg-surface-L2 text-left"
                    >
                      <p className="text-sm font-semibold leading-5 text-content-primary-bold">
                        {template.name}
                      </p>
                      <p className="text-xs leading-4 text-content-primary-subtle">
                        {template.description?.trim() ||
                          `${template.items.length} ${template.items.length === 1 ? 'field' : 'fields'}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
